//import { ProjectId } from 'module/api/models';
//import { TaskAPI } from 'module/api/tasks';
//import { getRandomColor } from 'module/helpers/get-random-color';
import { FieldValueType } from 'module/api/field/types';
import { isChildNode } from 'module/helpers/is-child-node';
import { FieldType } from 'module/types/filter-field/field/field';
import {
  // AssigneeField,
  //DateField,
  MultiSelectField,
  MultiUserField,
  SingleSelectField,
  SingleUserField,
  DateField,
  // SourceType,
} from 'module/types/filter-field/field/filterable-field';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { OutsideClickHandler } from 'module/ui/components/atoms/outside-click-handler';
import { OzoneFonts } from 'module/ui/components/atoms/ozone/atoms/typography/core';
import {
  getFieldValue,
  isSelect,
} from 'module/ui/components/molecules/field-editable-value';
import { DateFieldEditorPopover } from 'module/ui/components/molecules/field-editors/date/date-field-editor-popover';
import { MultiSelectFieldEditorPopover } from 'module/ui/components/molecules/field-editors/multi-select/multi-select-field-editor-popover';
import { MultiUserFieldEditorPopover } from 'module/ui/components/molecules/field-editors/multi-user/multi-user-field-editor-popover';
import { SingleSelectFieldEditorPopover } from 'module/ui/components/molecules/field-editors/single-select/single-select-field-editor-popover';
import { SingleUserFieldEditorPopover } from 'module/ui/components/molecules/field-editors/single-user/single-user-field-editor-popover';
import { isEqual } from 'lodash';
import { Component, createRef, RefObject } from 'react';
import styled from 'styled-components';
import type { GridApi, ICellEditorParams } from 'ag-grid-community';

interface Props extends ICellEditorParams {
  api: GridApi;
  //data: Task;
  //value: TaskFieldValue;
  onUpdate?: (
    row: Record<string, unknown>,
    field: FilterableField,
    from: FieldValueType,
    to: FieldValueType,
    set: (final: Record<string, unknown>) => void,
  ) => void;
}

const SingleUserEditor = styled(SingleUserFieldEditorPopover)`
  padding: 0 !important;

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const MultiUserEditor = styled(MultiUserFieldEditorPopover)`
  padding: 0 !important;

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const SingleSelectEditor = styled(SingleSelectFieldEditorPopover)`
  padding: 0 !important;
  caret-color: rgba(0, 0, 0, 0);

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const MultiSelectEditor = styled(MultiSelectFieldEditorPopover)`
  padding: 0 !important;

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const DateEditor = styled(DateFieldEditorPopover)`
  padding: 0 !important;
`;

interface State {
  value: FilterableField;
  id: string;
  width?: number;
  //projectPhases?: ProjectPhase[];
  isLoading?: boolean;
  dependencyModalVisible: boolean;
}

const KeyHandler = styled.div`
  display: flex;
  flex: 1;

  & > * {
    display: flex;
    flex: 1;
  }

  & > .ozone-select-container {
    padding: 0;
  }

  .ozone-select__menu {
    margin: 0;
  }

  .ant-picker-dropdown[style] {
    top: 85px !important;
  }
`;

const Wrapper = styled(OutsideClickHandler)`
  display: flex;
  flex: 1;
`;

const optionsGetter = (field: FilterableField) => {
  return [{ options: field.metaData?.options }];
};

const getStateValue = (field: FilterableField, props: Props) => {
  const { value: propValue } = props;
  let value = propValue;

  if (propValue == null) {
    return null;
  }
  if (field.fieldName === 'status' && typeof propValue === 'object') {
    value = (propValue as any).status as number;
  }
  if (field.fieldName === 'dueDate' && typeof propValue === 'object') {
    value = (propValue as any).dueDate as number;
  }

  return value;
};

const ConditionedWrapper = (props) => {
  const { data } = props;
  const disabled = data?.field_type === FieldType.Assignee;

  // allowed for multiline text richtext toolbar, and hyperlink form
  const isChild = isChildNode([
    'ck-toolbar-container',
    'ck-link-form',
    'ai-fill-selector-popup',
  ]);

  return <Wrapper {...props} allowed={isChild} disabled={disabled} />;
};

const applyReGrouping = (api: GridApi, update: any[]) => {
  setTimeout(() => {
    if (api.getModel().getType() === 'clientSide') {
      api.applyTransaction({
        update,
      });
    }
  }, 10);
};

export const getEditor = (field: FilterableField) => {
  const { type } = field;
  const Editor = getFieldValue(type);
  const selectBased = isSelect(type);

  // eslint-disable-next-line react/display-name
  return class extends Component<Props, State> {
    ref: RefObject<any>;

    keyHandlerRef: RefObject<HTMLDivElement>;

    constructor(props: Props) {
      super(props);

      this.state = {
        value: {
          ...field,
          value: getStateValue(field, props),
        } as any,
        id: `editor_${props.node.id}`,
        width:
          type === FieldType.Date
            ? 276
            : type === FieldType.Assignee ||
                type === FieldType.SingleUser ||
                type === FieldType.MultiUser ||
                (type === FieldType.SingleSelect &&
                  field.fieldName.toLowerCase() !== 'status') ||
                type === FieldType.MultiSelect
              ? 296
              : undefined,
        dependencyModalVisible: false,
      };
      this.ref = createRef();
      this.keyHandlerRef = createRef<HTMLDivElement>();
    }

    onDependencyModalToggle = () => {
      this.setState(({ dependencyModalVisible }) => ({
        dependencyModalVisible: !dependencyModalVisible,
      }));
    };

    commit = () => {
      const { node, api, column } = this.props;

      //setMarkerForDataDog(ClickEventTags.FieldEdit);

      if (node.id === 'provisional') {
        api.stopEditing();
        if (node.rowIndex != null) {
          api.setFocusedCell(node.rowIndex, column);
        }

        return;
      }
      this.update(true);
    };

    initialise = () => {
      this.focus();
      this.allowNewLineInMultiLine();
    };

    allowNewLineInMultiLine = () => {
      const { id } = this.state;

      if (type !== FieldType.MultiLineText) {
        return;
      }
      const element = document.querySelector(`#${id}`) as HTMLInputElement;

      element?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === 'NumpadEnter') {
          event.stopPropagation();
        }
      });
    };

    focusInput = () => {
      const { id } = this.state;
      const element = document.querySelector(`#${id}`) as HTMLInputElement;

      element?.focus();
      if (type === FieldType.Boolean) {
        element?.click();
      }
    };

    focusSelect = () => {
      const { ref } = this;

      ref.current?.select?.focus();
    };

    focus = () => {
      const { node } = this.props;
      const { ref } = this;

      if (
        type === FieldType.Date ||
        type === FieldType.Assignee ||
        type === FieldType.SingleUser ||
        type === FieldType.MultiUser ||
        (type === FieldType.SingleSelect &&
          field.fieldName.toLowerCase() !== 'status') ||
        type === FieldType.MultiSelect
      ) {
        ref.current?.focus?.();
      } else if (selectBased && node.id !== 'provisional') {
        this.focusSelect();
      } else {
        this.focusInput();
      }
    };

    getValue = () => {
      const { value } = this.state;

      return value.value;
    };

    onChange = (latest: undefined) => {
      const isSingleEditField =
        type === FieldType.SingleSelect ||
        type === FieldType.SingleUser ||
        type === FieldType.Date ||
        type === FieldType.Boolean ||
        field.fieldName === 'effort' ||
        field.fieldName === 'trackedMinutes';

      if (isSingleEditField) {
        // setMarkerForDataDog(ClickEventTags.FieldEdit);
      }

      const {
        value: { value: current },
      } = this.state;
      const { node } = this.props;
      const normalized = current == null ? null : current;

      if (
        latest === normalized &&
        !(node.id === 'provisional' && field.fieldName === 'status')
      ) {
        return;
      }

      this.setState(
        ({ value }) =>
          ({
            value: {
              ...value,
              value: latest == null ? null : latest,
            },
          }) as any,
        () => {
          if (isSingleEditField) {
            this.update(true);
          }
        },
      );
    };

    update = (stopEditing?: boolean, callback?: VoidFunction) => {
      const {
        value: { value: current },
        dependencyModalVisible,
      } = this.state;
      const { node, value: previous, api, column, data, onUpdate } = this.props;

      applyReGrouping(api, [{ ...data, [field.fieldName]: current }]);
      // If previous and current input are same irrespective of field type, dont update the field
      // For effort field, even if u chumma click on the cell and click outside again, the value will be set to -1 (if the initial value was null)
      // So for effort field alone, separate handling must be done
      if (
        field?.fieldName !== 'effort' &&
        field?.fieldName !== 'trackedMinutes'
      ) {
        if (!isEqual(previous ?? null, current ?? null)) {
          onUpdate?.(data, field, previous, (current ?? null) as any, () => {
            callback?.();
          });
          node.updateData({ ...data, [field.fieldName]: current });
        }
      } else if (
        !isEqual(previous ?? null, current ?? null) ||
        (previous == null && current === -1)
      ) {
        onUpdate?.(data, field, previous, (current ?? null) as any, () => {
          callback?.();
        });
        node.updateData({ ...data, [field.fieldName]: current });
      }

      if (stopEditing && node.id !== 'provisional' && !dependencyModalVisible) {
        api.stopEditing();
        if (node.rowIndex != null) {
          api.setFocusedCell(node.rowIndex, column);
        }
      }
    };

    afterGuiAttached = () => {
      this.initialise();
    };

    onAssigneeEditorClose = () => {
      const { api, node, data } = this.props;

      node.updateData({ ...data });
      api.stopEditing();
    };

    onAssigneeChangesDone = () => {
      this.commit();
    };

    /* onAssigneeChangesSave = (callback: VoidFunction) => {
      this.update(false, callback);
    }; */

    getDisabledDate = () => {
      //const { data } = this.props;
      //const { startDate, dueDate, startDateActual, dueDateActual } = data;

      if (field.customField) {
        return false;
      }
      /*if (
        field.fieldName === ProjectFieldsName.startDateActual &&
        dueDateActual
      ) {
        return disabledStartDate(current, moment(dueDateActual as string));
      }
      if (
        field.fieldName === ProjectFieldsName.dueDateActual &&
        startDateActual
      ) {
        return disabledDueDate(current, moment(startDateActual as string));
      }
      const isStartDatedField = field.fieldName.toLowerCase() === 'startdate';

      if (isStartDatedField && dueDate) {
        return disabledStartDate(current, moment(dueDate));
      }
      if (!isStartDatedField && startDate) {
        return disabledDueDate(current, moment(startDate));
      }*/

      return false;
    };

    /*DummyView = ({ projectId }: { projectId: number }) => {
      const { data: projectPhases, isInitialLoading } = useProjectsPhasesQuery(
        projectId,

        this.state?.value?.fieldName === 'projectPhaseId',
      );

      useEffect(() => {
        this.setState({
          isLoading: isInitialLoading,
        });
        if (!isInitialLoading) {
          this.setState({
            projectPhases,
          });
        }
      }, [isInitialLoading, projectPhases]);

      return null;
    };*/

    refresh(params: Props) {
      const { value } = this.state;

      if (params.value !== value.value) {
        this.setState({
          value: { ...field, value: params.value as any },
        });
      }

      return true;
    }

    render() {
      const { data, node, column, context } = this.props;
      const column_width = column.getActualWidth();
      const {
        id,
        value,
        width = column_width,
        //dependencyModalVisible,
      } = this.state;
      const {
        // DummyView,
        onChange,
        ref,
        commit,
        keyHandlerRef,
        //onAssigneeEditorClose,
        //onAssigneeChangesSave,
        //onAssigneeChangesDone,
        //onDependencyModalToggle,
      } = this;
      /*const projectId = ProjectId(
        data.project || (data.projectId as unknown as number),
      )*/
      // const { projectPhases, isLoading } = this.state;

      /*if (value.fieldName === 'projectPhaseId') {
        (value.metaData as any).options = projectPhases
          ? projectPhases.map((e) => ({
              label: e.projectPhaseName,
              value: e.projectPhaseId,
              color: getRandomColor(e.projectPhaseName),
              active: true,
            }))
          : null;
      }*/

      //const { startDate, dueDate } = data;

      return (
        <ConditionedWrapper
          data={{
            column_width: column_width.toString(),
            width: width.toString(),
            field_type: type,
          }}
          width={width}
          onOutsideClick={commit}
          allowed={isChildNode([
            'ant-picker-dropdown',
            'ant-popover-content',
            'popover-multi-select-content',
          ])}
        >
          {/*<DummyView projectId={projectId[1]} />*/}
          <KeyHandler ref={keyHandlerRef}>
            {type === FieldType.Date ? (
              <DateEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as DateField}
                onChange={onChange as any}
              />
            ) : type === FieldType.SingleUser ? (
              <SingleUserEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as SingleUserField}
                onChange={onChange as any}
                isPrivate={data.private}
                optionsGetter={optionsGetter}
              />
            ) : type === FieldType.MultiUser ? (
              <MultiUserEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as MultiUserField}
                onChange={onChange as any}
                isPrivate={data.private}
                optionsGetter={optionsGetter}
              />
            ) : field.fieldName === 'phaseName' ? (
              <SingleSelectEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as SingleSelectField}
                onChange={onChange as any}
              />
            ) : type === FieldType.SingleSelect &&
              field.fieldName.toLowerCase() !== 'status' ? (
              <SingleSelectEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as SingleSelectField}
                onChange={onChange as any}
                //isLoading={isLoading}
              />
            ) : type === FieldType.MultiSelect ? (
              <MultiSelectEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as MultiSelectField}
                onChange={onChange as any}
              />
            ) : (
              <Editor
                provisional={node.id === 'provisional'}
                id={id}
                ref={ref}
                field={value as any as never}
                type={data.type}
                is_private={data.private}
                //project_id={projectId}
                onChange={onChange}
                editable
                onKeyDown={(event: { key: string }) =>
                  ['Enter', 'NumpadEnter', 'Tab'].includes(event.key)
                    ? commit()
                    : undefined
                }
                //onCreateMultiple={}
                //onCreate={}
                getPopupContainer={() => keyHandlerRef.current!}
                enableAiFills={context?.aiFills?.enabled}
              />
            )}
          </KeyHandler>
          <OzoneFonts />
        </ConditionedWrapper>
      );
    }
  };
};
